import { type ReactElement } from 'react'
import { type AppId, type CompanyId, isEmpty, isNotEmpty } from '@terros/common'
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { useConnectApp, useTranslation } from '@front/model'
import { MarkdownDisplay, NotReady, Page, TButton, TSection } from '@components'
import { CONNECT_MARKETPLACE_HREF, parseConnectAppId } from '../integration/util'
import { ConnectAppLogo } from '../integration/ui'
import { ConnectAccessGuard } from '../integration/access'

function ConnectMarketplaceDocumentationPage(): ReactElement {
  const { appId } = Route.useParams()
  const parsedAppId = parseConnectAppId(appId)

  if (!parsedAppId) {
    return (
      <ConnectAccessGuard>
        <NotReady error='Invalid app id' id='marketplace app' />
      </ConnectAccessGuard>
    )
  }

  return <DocumentationWorkspace appId={parsedAppId} />
}

function DocumentationWorkspace({ appId }: { appId: AppId }): ReactElement {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const appState = useConnectApp(appId)
  const app = appState.data?.app
  const appName = app?.name ?? t('App')

  return (
    <ConnectAccessGuard>
      <Page>
        <Page.Heading
          back
          breadcrumbs={[
            { href: CONNECT_MARKETPLACE_HREF, label: t('Marketplace') },
            { disabled: true, href: '#', label: t('Documentation') },
          ]}
          handleGoBack={() => navigate({ to: CONNECT_MARKETPLACE_HREF })}
          isFetching={appState.isFetching}
          refetch={() => {
            appState.refetch()
          }}
          leftSection={
            app && (
              <ConnectAppLogo avatarBlurhash={app.avatarBlurhash} avatarUrl={app.avatarUrl} name={appName} size='sm' />
            )
          }
          title={t('{{appName}} Documentation', { appName })}
        />
        <Page.Content scrollArea>
          {(appState.isLoading || appState.error || !appState.data) && (
            <NotReady
              data={appState.data}
              error={appState.error}
              id={t('marketplace app documentation')}
              isLoading={appState.isLoading}
            />
          )}
          {appState.data && isEmpty(app?.documentation) && (
            <Page.Hero
              icon='description'
              title={t('No documentation')}
              description={t('This marketplace app does not have documentation yet.')}
            />
          )}

          {isNotEmpty(app?.documentation) && <MarkdownDisplay>{app.documentation}</MarkdownDisplay>}
        </Page.Content>
      </Page>
    </ConnectAccessGuard>
  )
}

export const Route = createFileRoute('/integration/marketplace_/$appId/documentation/')({
  component: ConnectMarketplaceDocumentationPage,
})
